import { Clear } from '@mui/icons-material';
import { ListItem, useTheme } from '@mui/material';
import Button from '@mui/material/Button';
import { FC } from 'react';
import { emptyValue } from '@/types/detail';
import { ConfigurationVariable } from '@/types/metadata/metadata';
import { VariableRef } from '@/types/variable';
import { isVariable } from '@/util/typeGuards';
import { variablesListUtil } from '@/util/validation/variableslist';
import ConfirmPopover from '@/components/common/ConfirmPopover';
import List from '@/components/common/List';
import ListArrows from '@/components/common/ListArrows';
import { ParamProps } from './ParamRenderer';
import VariableView from './VariableView';

const ParamVariableList: FC<ParamProps> = (props) => {
  const theme = useTheme();
  const configuration = props.configuration as ConfigurationVariable;

  const initialValue = variablesListUtil().getValue(props.configuration, props.initialValue);

  const removeEntry = (indexToRemove: number, values: (VariableRef | emptyValue)[]) => {
    const newInitialValue = values.filter((_, index) => index !== indexToRemove);
    props.onChange([props.configuration.value, newInitialValue]);
  };

  return (
    <List
      subheader={configuration.value + (props.configuration.required ? ' *' : '')}
      {...(!!props.errors?.length && {
        subheaderSx: { color: theme.palette.error.main }
      })}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'start'
      }}
    >
      {initialValue?.map((value, index) => (
        <ListItem key={`varlist.${index}`}>
          <VariableView
            label={`${index + 1}`}
            subroutineId={props.parentId}
            type={configuration.subtype || 'any'}
            valueKey={configuration.value}
            onChange={(newVariable) => {
              initialValue[index] = newVariable ?? ({} as VariableRef);
              props.onChange([configuration.value, initialValue]);
            }}
            initialValue={value}
            allowEmpty
          />
          <ListArrows
            data={initialValue}
            index={index}
            onChange={(result) => props.onChange([props.configuration.value, result])}
          />
          <ConfirmPopover
            onConfirm={() => removeEntry(index, initialValue)}
            icon={<Clear />}
            skipConfirm={Object.keys(value).length === 0}
            text={<>Remove {isVariable(value) ? <code>{value.name}</code> : 'value'} from list?</>}
          />
        </ListItem>
      ))}
      <ListItem>
        <Button
          variant='contained'
          color='primary'
          onClick={() => {
            props.onChange([configuration.value, initialValue ? [...initialValue, {}] : [{}]]);
          }}
        >
          Add variable
        </Button>
      </ListItem>
    </List>
  );
};

export default ParamVariableList;
